<div class="p-6">
  <div class="space-y-6">
    <!-- Description -->
    @if (data().description) {
      <div>
        <label class="mb-1 block text-sm font-semibold text-gray-700">Description</label>
        <div class="text-base text-gray-500">
          <markdown [data]="data().description!" [katex]="true" [katexOptions]="katexOptions"></markdown>
        </div>
      </div>
    }

    <!-- Category and ID -->
    <div class="grid grid-cols-2 gap-6">
      @if (data().category) {
        <div>
          <label class="mb-1 block text-sm font-semibold text-gray-700">Category</label>
          <button
            [matMenuTriggerFor]="categoryMenu"
            class="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            <svg
              class="h-4 w-4 text-gray-600"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path [attr.d]="categorySvgPath()" />
            </svg>
            {{ data().category | titlecase }}
            <dojo-icon icon="circle-info" [size]="'xs'" class="ml-0.5 opacity-40"></dojo-icon>
          </button>
          <mat-menu #categoryMenu="matMenu" class="no-width-constraints">
            <div class="w-[560px] p-4" (click)="$event.stopPropagation()">
              <div class="mb-3 text-sm font-semibold text-gray-900">Representation Categories</div>
              <table class="w-full text-left text-xs">
                <tbody>
                  @for (cat of representationCategories; track cat.name) {
                    <tr class="border-b border-gray-100 last:border-0">
                      <td class="py-2 pr-3 align-top">
                        <div class="flex items-center gap-1.5">
                          <svg
                            class="h-4 w-4 text-gray-600"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path [attr.d]="cat.svgPath" />
                          </svg>
                          <span class="text-sm font-medium text-gray-800">{{ cat.name | titlecase }}</span>
                          @if (cat.name === data().category) {
                            <dojo-icon icon="circle-check" [size]="'xs'" class="text-green-500"></dojo-icon>
                          }
                        </div>
                        <div class="mt-1 flex flex-wrap gap-1 pl-[22px]">
                          @for (type of cat.types; track type) {
                            <code
                              class="rounded px-1.5 py-0.5 text-xs"
                              [ngClass]="fieldTypeService.getTypeStringBadgeClasses(type)"
                              >{{ type }}</code
                            >
                          }
                        </div>
                      </td>
                      <td class="py-2 align-top">
                        <div class="text-gray-700">{{ cat.description }}</div>
                        <div class="mt-0.5 text-gray-400">{{ cat.topics }}</div>
                        <div class="mt-1 rounded bg-gray-100 px-2 py-1 font-mono text-gray-600">
                          <markdown [data]="cat.example" [katex]="true" [katexOptions]="katexOptions"></markdown>
                        </div>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </mat-menu>
        </div>
      }
      <div>
        <label class="mb-1 block text-sm font-semibold text-gray-700">ID</label>
        <button
          [dojoCopyToClipboard]="data().id"
          class="group inline-flex cursor-pointer items-center gap-1.5 font-mono text-sm text-gray-500 hover:text-gray-700"
        >
          {{ data().id }}
          <dojo-icon icon="copy" [size]="'xs'" class="opacity-40 group-hover:opacity-100"></dojo-icon>
        </button>
      </div>
    </div>

    <!-- Model Form -->
    @if (data().model) {
      <div>
        <label class="mb-2 block text-sm font-semibold text-gray-700">Interface</label>
        @if (isRoot()) {
          <form [formGroup]="form()" class="space-y-3">
            @for (field of sortedFields(); track field.key) {
              <div
                class="rounded-md border p-3 transition-colors"
                [class.border-gray-300]="!field.isOptional"
                [class.bg-gray-100]="!field.isOptional"
                [class.border-gray-200]="field.isOptional"
                [class.bg-gray-50]="field.isOptional"
              >
                <div class="mb-2 flex items-center justify-between">
                  <label [for]="field.key" class="flex items-center gap-1.5 text-sm text-gray-800">
                    @if (field.def.label_type === 'latex') {
                      <span class="inline-block rounded bg-gray-200 px-1.5 py-0.5 text-gray-700">
                        <markdown
                          [data]="'$' + (field.def.label ?? field.key) + '$'"
                          [katex]="true"
                          [katexOptions]="katexOptions"
                        ></markdown>
                      </span>
                    } @else {
                      <code class="rounded bg-gray-200 px-1.5 py-0.5 font-mono text-xs text-gray-700">{{
                        field.def.label ?? field.key
                      }}</code>
                    }
                    @if (field.isOptional) {
                      <span class="text-xs font-normal text-gray-400">(optional)</span>
                    }
                  </label>
                  <exam-type-badge [field]="field.def"></exam-type-badge>
                </div>

                @if (field.def.description) {
                  <div class="mb-2 text-xs text-gray-500">
                    <markdown [data]="field.def.description" [katex]="true" [katexOptions]="katexOptions"></markdown>
                  </div>
                }

                <!-- Input Controls -->
                @if (field.def.type === 'constant') {
                  <div class="rounded border border-gray-200 bg-white px-2 py-1 font-mono text-sm text-gray-600">
                    {{ fieldTypeService.getConstantSymbol(field.def['value']) }}
                  </div>
                } @else {
                  @if (formControls().get(field.key); as ctrl) {
                    @if (field.def.type === 'number') {
                      <dojo-number-input
                        [formCtrl]="ctrl"
                        size="s"
                        [min]="field.def['min'] ?? null"
                        [max]="field.def['max'] ?? null"
                        placeholder="Enter value..."
                      ></dojo-number-input>
                    } @else if (field.def.type === 'enum') {
                      <dojo-single-select
                        [formCtrl]="ctrl"
                        size="s"
                        [options]="fieldTypeService.getOptions(field.def['options'])"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select..."
                      ></dojo-single-select>
                    } @else if (field.def.type === 'boolean') {
                      <dojo-single-select
                        [formCtrl]="ctrl"
                        size="s"
                        [options]="booleanOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select..."
                      ></dojo-single-select>
                    } @else {
                      <dojo-text-input [formCtrl]="ctrl" size="s" placeholder="Enter value..."></dojo-text-input>
                      @if (fieldTypeService.isLatexField(field.def) && ctrl.value) {
                        <div class="mt-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                          <markdown
                            [data]="'$' + ctrl.value + '$'"
                            [katex]="true"
                            [katexOptions]="katexOptions"
                          ></markdown>
                        </div>
                      }
                    }
                  }
                }
              </div>
            }

            <!-- Execute Button -->
            <dojo-button [size]="'s'" (click)="onExecute()" [disabled]="form().invalid" class="w-full">
              <dojo-icon icon="play" class="mr-2"></dojo-icon>
              <span>Execute</span>
            </dojo-button>
          </form>
        } @else {
          <!-- Read-only Model Definitions (Always Visible) -->
          <div class="space-y-3">
            @for (field of sortedFields(); track field.key) {
              <div
                class="rounded-md border p-3 transition-colors"
                [class.border-gray-300]="!field.isOptional"
                [class.bg-gray-100]="!field.isOptional"
                [class.border-gray-200]="field.isOptional"
                [class.bg-gray-50]="field.isOptional"
              >
                <div class="mb-1 flex items-center justify-between">
                  <label class="flex items-center gap-1.5 text-sm text-gray-800">
                    @if (field.def.label_type === 'latex') {
                      <span class="inline-block rounded bg-gray-200 px-1.5 py-0.5 text-gray-700">
                        <markdown
                          [data]="'$' + (field.def.label ?? field.key) + '$'"
                          [katex]="true"
                          [katexOptions]="katexOptions"
                        ></markdown>
                      </span>
                    } @else {
                      <code class="rounded bg-gray-200 px-1.5 py-0.5 font-mono text-xs text-gray-700">{{
                        field.def.label ?? field.key
                      }}</code>
                    }
                    @if (field.isOptional) {
                      <span class="text-xs font-normal text-gray-400">(optional)</span>
                    }
                  </label>
                  <exam-type-badge [field]="field.def"></exam-type-badge>
                </div>
                @if (field.def.description) {
                  <div class="text-xs text-gray-500">
                    <markdown [data]="field.def.description" [katex]="true" [katexOptions]="katexOptions"></markdown>
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>
    }

    <!-- Transformation Flow (Derived Only) -->
    @if (!isRoot()) {
      <div class="border-t border-gray-200 pt-2">
        @if (runtimeValue()) {
          <!-- Tabs for multiple derivation methods -->
          @if (hasMultipleProcedures()) {
            <div class="mb-4">
              <mat-tab-group
                [selectedIndex]="selectedProcedureIndex()"
                (selectedIndexChange)="onTabChange($event)"
                class="custom-mat-tab size-m"
              >
                @for (proc of sourceProcedures(); track proc.id; let i = $index) {
                  <mat-tab [label]="proc.name"></mat-tab>
                }
              </mat-tab-group>
            </div>
          }

          <!-- Unified Flow: Input → Procedure → Output -->
          <div class="relative">
            <!-- Flow Steps -->
            <div class="space-y-0">
              <!-- Step 1: Input -->
              @if (inputRepresentationsWithValues().length > 0) {
                <div class="relative pb-6">
                  <!-- Connector Line -->
                  <div class="absolute bottom-0 left-4 top-6 w-px bg-gray-300"></div>
                  <!-- Step Indicator -->
                  <div class="flex items-start gap-3">
                    <div
                      class="z-5 relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500"
                    >
                      <dojo-icon icon="cube" [size]="'xs'"></dojo-icon>
                    </div>
                    <div class="flex-1 pt-0.5">
                      <!-- Single input representation: link at title level -->
                      @if (hasSingleInputRepresentation()) {
                        <button
                          (click)="navigateToNode.emit(singleInputRepresentationWithValues().id)"
                          class="mb-2 text-sm font-medium text-gray-900 transition-colors hover:text-blue-600"
                        >
                          Input
                          <dojo-icon
                            icon="arrow-up-right-from-square"
                            [size]="'xs'"
                            class="ml-1 opacity-50"
                          ></dojo-icon>
                        </button>
                        <div class="flex flex-wrap gap-1.5">
                          @for (item of singleInputRepresentationWithValues().values | keyvalue; track item.key) {
                            <span class="inline-flex rounded-md bg-gray-100 px-2 py-1 font-mono text-xs text-gray-700">
                              <markdown
                                [data]="'$' + getFieldLabel(item.key) + ' = ' + item.value + '$'"
                                [katex]="true"
                                [katexOptions]="katexOptions"
                              ></markdown>
                            </span>
                          }
                        </div>
                      } @else {
                        <!-- Multiple input representations: group by representation -->
                        <span class="mb-2 text-sm font-medium text-gray-900">Input</span>
                        <div class="mt-2 space-y-2">
                          @for (inputRep of inputRepresentationsWithValues(); track inputRep.id) {
                            <div class="rounded-lg border border-gray-200 bg-white">
                              <button
                                (click)="navigateToNode.emit(inputRep.id)"
                                class="flex w-full flex-col text-left transition-colors hover:bg-gray-50"
                              >
                                <div class="flex w-full items-center gap-2 px-3 py-2">
                                  <svg
                                    class="h-4 w-4 shrink-0 text-gray-500"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  >
                                    <path [attr.d]="inputRep.svgPath" />
                                  </svg>
                                  <span class="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">{{
                                    inputRep.name
                                  }}</span>
                                  <dojo-icon
                                    icon="arrow-up-right-from-square"
                                    [size]="'xs'"
                                    class="shrink-0 opacity-40"
                                  ></dojo-icon>
                                </div>
                                <div class="mx-3 border-b border-gray-200"></div>
                              </button>
                              <div class="flex flex-wrap gap-1.5 p-2">
                                @for (item of inputRep.values | keyvalue; track item.key) {
                                  <span
                                    class="inline-flex rounded-md bg-gray-100 px-2 py-1 font-mono text-xs text-gray-700"
                                  >
                                    <markdown
                                      [data]="
                                        '$' +
                                        getFieldLabelFromModel(item.key, inputRep.model) +
                                        ' = ' +
                                        item.value +
                                        '$'
                                      "
                                      [katex]="true"
                                      [katexOptions]="katexOptions"
                                    ></markdown>
                                  </span>
                                }
                              </div>
                            </div>
                          }
                        </div>
                      }
                    </div>
                  </div>
                </div>
              }

              <!-- Step 2: Via Procedure -->
              @if (selectedProcedure(); as proc) {
                <div class="relative pb-6">
                  <!-- Connector Line -->
                  <div class="absolute bottom-0 left-4 top-6 w-px bg-gray-300"></div>
                  <!-- Step Indicator -->
                  <div class="flex items-start gap-3">
                    <div
                      class="z-5 relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500"
                    >
                      <dojo-icon icon="gears" [size]="'xs'"></dojo-icon>
                    </div>
                    <div class="flex-1 pt-0.5">
                      <button
                        (click)="navigateToNode.emit(proc.id)"
                        class="text-sm font-medium text-gray-900 transition-colors hover:text-blue-600"
                      >
                        {{ proc.name }}
                        <dojo-icon icon="arrow-up-right-from-square" [size]="'xs'" class="ml-1 opacity-50"></dojo-icon>
                      </button>
                      @if (proc.formula_latex) {
                        <div class="mt-1 text-sm text-gray-500">
                          <markdown
                            [data]="'$' + proc.formula_latex + '$'"
                            [katex]="true"
                            [katexOptions]="katexOptions"
                          ></markdown>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              }

              <!-- Step 3: Output -->
              <div class="relative">
                <!-- Step Indicator -->
                <div class="flex items-start gap-3">
                  <div
                    class="z-5 relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500"
                  >
                    <dojo-icon icon="equals" [size]="'xs'"></dojo-icon>
                  </div>
                  <div class="flex-1 pt-0.5">
                    <span class="text-sm font-medium text-gray-900">Output</span>
                    <!-- Primary Result Display -->
                    <div class="mt-2 rounded-lg border border-green-500 bg-green-50 p-3">
                      <div class="text-md font-light text-gray-900">
                        <markdown [data]="renderedTemplate()" [katex]="true" [katexOptions]="katexOptions"></markdown>
                      </div>
                    </div>
                    <!-- Breakdown with connection lines -->
                    <div class="relative mt-4 pl-7">
                      <!-- Vertical connector line (extends up to result box, ends at last item's center) -->
                      <div class="absolute -top-4 bottom-0 left-[13px] w-px bg-gray-300"></div>
                      <div class="space-y-2">
                        @for (field of sortedFields(); track field.key; let last = $last) {
                          @if (runtimeValue()![field.key] !== undefined) {
                            <div
                              class="relative flex items-stretch [&:last-child_.line-blocker]:block"
                              [class.last-item]="last"
                            >
                              <!-- White blocker to hide bottom half of line for last item -->
                              <div
                                class="line-blocker absolute -left-[16px] bottom-0 top-1/2 hidden w-[4px] bg-white"
                              ></div>
                              <!-- Content box with horizontal connector via before pseudo-element -->
                              <div
                                class="relative flex-1 rounded-md border p-3 before:absolute before:-left-[15px] before:top-1/2 before:h-px before:w-[15px] before:-translate-y-1/2 before:bg-gray-300"
                                [class.border-gray-300]="!field.isOptional"
                                [class.bg-gray-100]="!field.isOptional"
                                [class.border-gray-100]="field.isOptional"
                                [class.bg-gray-50]="field.isOptional"
                              >
                                <div class="flex items-start justify-between">
                                  <div class="flex-1 pr-4">
                                    <span
                                      class="flex items-center gap-1.5 text-sm"
                                      [class.text-gray-900]="!field.isOptional"
                                      [class.text-gray-700]="field.isOptional"
                                    >
                                      @if (field.def.label_type === 'latex') {
                                        <span class="inline-block rounded bg-gray-200 px-1.5 py-0.5 text-gray-700">
                                          <markdown
                                            [data]="'$' + (field.def.label ?? field.key) + '$'"
                                            [katex]="true"
                                            [katexOptions]="katexOptions"
                                          ></markdown>
                                        </span>
                                      } @else {
                                        <code
                                          class="rounded bg-gray-200 px-1.5 py-0.5 font-mono text-xs text-gray-700"
                                          >{{ field.def.label ?? field.key }}</code
                                        >
                                      }
                                      @if (field.isOptional) {
                                        <span class="text-xs font-normal text-gray-400">(optional)</span>
                                      }
                                    </span>
                                    <div class="mt-0.5 text-xs text-gray-500">
                                      <markdown
                                        [data]="field.def.description"
                                        [katex]="true"
                                        [katexOptions]="katexOptions"
                                      ></markdown>
                                    </div>
                                  </div>
                                  <div class="shrink-0 text-right">
                                    @if (enumFields().has(field.key)) {
                                      <span
                                        class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium"
                                        [ngClass]="fieldTypeService.getTypeBadgeClasses(field.def)"
                                      >
                                        {{ runtimeValue()![field.key] }}
                                      </span>
                                    } @else {
                                      <span class="text-sm text-gray-900">
                                        <markdown
                                          [data]="formattedRuntimeValues().get(field.key) ?? ''"
                                          [katex]="true"
                                          [katexOptions]="katexOptions"
                                        ></markdown>
                                      </span>
                                    }
                                  </div>
                                </div>
                              </div>
                            </div>
                          }
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        } @else {
          <!-- Placeholder -->
          <div class="flex flex-col items-center justify-center py-8 text-center">
            <div class="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <dojo-icon [size]="20" icon="cogs" class="text-gray-400"></dojo-icon>
            </div>
            <p class="mb-3 text-sm text-gray-500">Execute the root node to see the transformation flow.</p>
            <dojo-button (click)="autoExecuteRoot.emit()">
              <dojo-icon icon="play" class="mr-2"></dojo-icon>
              <span>Execute</span>
            </dojo-button>
          </div>
        }
      </div>
    }
  </div>
</div>
